import React, { useEffect } from 'react';

import { useForm } from 'react-hook-form';

import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { BorrowerRole } from 'enums/BorrowerRole';
import {
  getEmploymentsAmount,
  getIsAddressInformationDisabled,
  getEmploymentVariableName,
  getIsInitialValueNotNull,
  getIsEmploymentSectionCompleted,
} from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.utils';
import Input from 'components/Inputs/Input';
import {
  CurrentEmploymentVariable,
  CurrentEmploymentInputLabel,
  PreviousEmploymentVariable,
} from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.enums';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';
import NumberInput from 'components/Inputs/NumberInput';
import { SearchLocationInput } from 'components/SearchLocationInput';
import { Button } from 'components/Buttons/Button';
import { addressRegistration } from 'prequalificationV2/utils/registrationUtils';
import { BorrowerEmploymentStatus } from 'enums/ApplicationsVariables';
import Government from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/EmploymentInformation/EmploymentStatusInformation/GovernmentInformation';
import ActiveMilitary from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/EmploymentInformation/EmploymentStatusInformation/ActiveMilitaryInformation';
import RetiredMilitary from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/EmploymentInformation/EmploymentStatusInformation/RetiredMilitaryInformation';
import RetiredNonMilitary from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/EmploymentInformation/EmploymentStatusInformation/RetiredNonMilitaryInformation';
import {
  getErrorMessageForRequiredFields,
  getStreetNameFromStreetAddress,
  getStreetNumberFromStreetAddress,
} from 'prequalificationV2/utils/stringUtils';
import { getMessageForInvalidFields, validatePhoneNumber } from 'prequalificationV2/utils/validationUtils';
import { DateFormat } from 'enums/DateFormat';
import { isValidDateBeforeNow, parseMonthYearToDate } from 'prequalificationV2/utils/dateUtils';
import { setDefaultEmploymentsValues } from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/EmploymentInformation/employmentInformation.utils';
import PreviousEmploymentInformation from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/EmploymentInformation/PreviousEmploymentInformation/PreviousEmploymentInformation';
import { LoadingType, setLoadingState } from 'prequalificationV2/actions';
import { DashboardTabName } from 'enums/DashboardTabName';
import { updateApplicationInStore } from 'prequalificationV2/utils/updateApplicationInStore';
import { FailureNotificationType } from 'notifications';
import * as notificationManager from 'notifications';
import { EmploymentInformationDto, EmploymentStatusInformationDto, hilEmploymentsApi } from 'api/EmploymentsApi';
import styles from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/InformationTabs.module.scss';
import { Address } from 'prequalificationV2/api/ApplicationApi';

interface EmploymentsInformationProps {
  borrowerType: BorrowerRole;
}

const EmploymentsInformation = ({ borrowerType }: EmploymentsInformationProps) => {
  const dispatch = useAppDispatch();

  const { variables, applicationId, dashboardAdditionalInfo } = useAppSelector((state) => state.application);
  const applicationVariables = variables!;
  const isCurrentEmploymentsRequired =
    borrowerType === BorrowerRole.Borrower
      ? dashboardAdditionalInfo?.isBorrowerCurrentEmploymentsRequired
      : dashboardAdditionalInfo?.isCoBorrowerCurrentEmploymentsRequired;
  const isPartialCurrentEmploymentsRequired =
    borrowerType === BorrowerRole.Borrower
      ? dashboardAdditionalInfo?.isPartialBorrowerCurrentEmploymentsRequired
      : dashboardAdditionalInfo?.isPartialCoBorrowerCurrentEmploymentsRequired;
  const isCurrentEmploymentsCompleted =
    borrowerType === BorrowerRole.Borrower
      ? dashboardAdditionalInfo?.isBorrowerCurrentEmploymentsCompleted
      : dashboardAdditionalInfo?.isCoBorrowerCurrentEmploymentsCompleted;
  const isEmploymentStatusInformationRequired =
    borrowerType === BorrowerRole.Borrower
      ? dashboardAdditionalInfo?.isBorrowerEmploymentStatusInformationRequired
      : dashboardAdditionalInfo?.isCoBorrowerEmploymentStatusInformationRequired;
  const isEmploymentStatusInformationCompleted =
    borrowerType === BorrowerRole.Borrower
      ? dashboardAdditionalInfo?.isBorrowerEmploymentStatusInformationCompleted
      : dashboardAdditionalInfo?.isCoBorrowerEmploymentStatusInformationCompleted;
  const isPreviousEmploymentRequired =
    borrowerType === BorrowerRole.Borrower
      ? dashboardAdditionalInfo?.isBorrowerPreviousEmploymentRequired
      : dashboardAdditionalInfo?.isCoBorrowerPreviousEmploymentRequired;
  const isPreviousEmploymentCompleted =
    borrowerType === BorrowerRole.Borrower
      ? dashboardAdditionalInfo?.isBorrowerPreviousEmploymentCompleted
      : dashboardAdditionalInfo?.isCoBorrowerPreviousEmploymentCompleted;

  const employmentsAmount = getEmploymentsAmount(borrowerType, applicationVariables);

  const formMethods = useForm({
    defaultValues: setDefaultEmploymentsValues(applicationVariables, employmentsAmount, borrowerType),
    shouldUnregister: false,
  });

  const {
    register,
    unregister,
    handleSubmit,
    clearErrors,
    formState: { errors },
    watch,
    control,
    setValue,
    formState,
    trigger,
    getValues,
  } = formMethods;
  const watcher = watch();

  useEffect(() => {
    if (!isCurrentEmploymentsRequired && !isPartialCurrentEmploymentsRequired) {
      return;
    }

    for (let index = 1; index <= employmentsAmount; index++) {
      if (isCurrentEmploymentsRequired || isPartialCurrentEmploymentsRequired) {
        register(getEmploymentVariableName(CurrentEmploymentVariable.HireDate, borrowerType, index), {
          required: {
            value: !watcher[getEmploymentVariableName(CurrentEmploymentVariable.HireDate, borrowerType, index)],
            message: getErrorMessageForRequiredFields(CurrentEmploymentInputLabel.HireDate),
          },
          validate: (value) => {
            return (
              isValidDateBeforeNow(value, DateFormat.MonthYear) ||
              getMessageForInvalidFields(CurrentEmploymentInputLabel.HireDate)
            );
          },
        });
        register(getEmploymentVariableName(CurrentEmploymentVariable.Position, borrowerType, index), {
          required: {
            value: !watcher[getEmploymentVariableName(CurrentEmploymentVariable.Position, borrowerType, index)],
            message: getErrorMessageForRequiredFields(CurrentEmploymentInputLabel.Position),
          },
        });
      }

      if (isCurrentEmploymentsRequired) {
        register(getEmploymentVariableName(CurrentEmploymentVariable.EmployerPhone, borrowerType, index), {
          required: {
            value: !watcher[getEmploymentVariableName(CurrentEmploymentVariable.EmployerPhone, borrowerType, index)],
            message: getErrorMessageForRequiredFields(CurrentEmploymentInputLabel.EmployerPhone),
          },
          validate: (value) => {
            return validatePhoneNumber(value);
          },
        });
        addressRegistration(
          register,
          {
            streetName: getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, index),
            cityName: getEmploymentVariableName(CurrentEmploymentVariable.City, borrowerType, index),
            stateName: getEmploymentVariableName(CurrentEmploymentVariable.State, borrowerType, index),
            zipName: getEmploymentVariableName(CurrentEmploymentVariable.Zip, borrowerType, index),
          },
          {
            streetLabel: CurrentEmploymentInputLabel.Address,
            cityLabel: CurrentEmploymentInputLabel.City,
            stateLabel: CurrentEmploymentInputLabel.State,
            zipLabel: CurrentEmploymentInputLabel.Zip,
          },
        );
      }
    }
  }, [register, unregister, watcher]);

  const saveEmploymentsInformation = handleSubmit(async () => {
    try {
      dispatch(
        setLoadingState({
          type: `${LoadingType.RequiredInformationUpdating}${DashboardTabName.AdditionalInformation}`,
          loading: true,
        }),
      );

      const updatedApplication = await hilEmploymentsApi.updateAdditionalEmploymentInformation({
        applicationId: applicationId as string,
        borrowerType: borrowerType,
        ...(isEmploymentStatusInformationRequired && !isEmploymentStatusInformationCompleted
          ? {
              employmentStatusInformation: {
                governmentPaygrade:
                  watcher[getEmploymentVariableName(CurrentEmploymentVariable.GovernmentPaygrade, borrowerType)]
                    ?.label || null,
                branchOfService:
                  watcher[getEmploymentVariableName(CurrentEmploymentVariable.BranchOfService, borrowerType)]?.label ||
                  null,
                militaryPaygrade:
                  watcher[getEmploymentVariableName(CurrentEmploymentVariable.MilitaryPaygrade, borrowerType)]?.label ||
                  null,
                branchOfServiceRetired:
                  watcher[getEmploymentVariableName(CurrentEmploymentVariable.RetiredBranchOfService, borrowerType)]
                    ?.label || null,
                militaryPaygradeRetired:
                  watcher[getEmploymentVariableName(CurrentEmploymentVariable.RetiredMilitaryPaygrade, borrowerType)]
                    ?.label || null,
                retirementDate: watcher[
                  getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType)
                ]
                  ? parseMonthYearToDate(
                      watcher[getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType)],
                    )
                  : null,
              } as EmploymentStatusInformationDto,
            }
          : {}),
        ...(isPreviousEmploymentRequired && !isPreviousEmploymentCompleted
          ? {
              previousEmploymentInformation: {
                name: watcher[getEmploymentVariableName(PreviousEmploymentVariable.EmployerName, borrowerType)],
                position: watcher[getEmploymentVariableName(PreviousEmploymentVariable.Position, borrowerType)],
                phone: watcher[getEmploymentVariableName(PreviousEmploymentVariable.EmployerPhone, borrowerType)],
                hireDate: watcher[getEmploymentVariableName(PreviousEmploymentVariable.HireDate, borrowerType)]
                  ? parseMonthYearToDate(
                      watcher[getEmploymentVariableName(PreviousEmploymentVariable.HireDate, borrowerType)],
                    )
                  : null,
                status: watcher[getEmploymentVariableName(PreviousEmploymentVariable.EmploymentStatus, borrowerType)],
                address: {
                  streetNumber: getStreetNumberFromStreetAddress(
                    watcher[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType)],
                  ),
                  streetName: getStreetNameFromStreetAddress(
                    watcher[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType)],
                  ),
                  city: watcher[getEmploymentVariableName(PreviousEmploymentVariable.City, borrowerType)],
                  stateOrProvince: watcher[getEmploymentVariableName(PreviousEmploymentVariable.State, borrowerType)],
                  zipOrPostalCode: watcher[getEmploymentVariableName(PreviousEmploymentVariable.Zip, borrowerType)],
                } as Address,
              } as EmploymentInformationDto,
            }
          : {}),
        ...((isCurrentEmploymentsRequired || isPartialCurrentEmploymentsRequired) && !isCurrentEmploymentsCompleted
          ? {
              employmentInformation: new Array(employmentsAmount).fill('').map((_, index) => {
                const currentIndex = index + 1;

                return {
                  name: watcher[
                    getEmploymentVariableName(CurrentEmploymentVariable.EmployerName, borrowerType, currentIndex)
                  ],
                  phone:
                    watcher[
                      getEmploymentVariableName(CurrentEmploymentVariable.EmployerPhone, borrowerType, currentIndex)
                    ],
                  hireDate: watcher[
                    getEmploymentVariableName(CurrentEmploymentVariable.HireDate, borrowerType, currentIndex)
                  ]
                    ? parseMonthYearToDate(
                        watcher[
                          getEmploymentVariableName(CurrentEmploymentVariable.HireDate, borrowerType, currentIndex)
                        ],
                      )
                    : null,
                  position:
                    watcher[getEmploymentVariableName(CurrentEmploymentVariable.Position, borrowerType, currentIndex)],
                  address: {
                    streetNumber: getStreetNumberFromStreetAddress(
                      watcher[getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, currentIndex)],
                    ),
                    streetName: getStreetNameFromStreetAddress(
                      watcher[getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, currentIndex)],
                    ),
                    city: watcher[
                      getEmploymentVariableName(CurrentEmploymentVariable.City, borrowerType, currentIndex)
                    ],

                    stateOrProvince:
                      watcher[getEmploymentVariableName(CurrentEmploymentVariable.State, borrowerType, currentIndex)],
                    zipOrPostalCode:
                      watcher[getEmploymentVariableName(CurrentEmploymentVariable.Zip, borrowerType, currentIndex)],
                  } as Address,
                } as EmploymentInformationDto;
              }),
            }
          : {}),
      });

      updateApplicationInStore(updatedApplication, dispatch);
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      dispatch(
        setLoadingState({
          type: `${LoadingType.RequiredInformationUpdating}${DashboardTabName.AdditionalInformation}`,
          loading: false,
        }),
      );
    }
  });

  const renderEmployments = () => {
    return new Array(employmentsAmount).fill('').map((_, index) => {
      const currentIndex = index + 1;
      const isOnlyPartial = !isCurrentEmploymentsRequired && isPartialCurrentEmploymentsRequired;

      return (
        <div key={currentIndex} className={styles.employmentContainer}>
          <div className={styles.formRow}>
            <Input
              label={CurrentEmploymentInputLabel.EmployerName}
              name={getEmploymentVariableName(CurrentEmploymentVariable.EmployerName, borrowerType, currentIndex)}
              value={
                watcher[getEmploymentVariableName(CurrentEmploymentVariable.EmployerName, borrowerType, currentIndex)]
              }
              required={false}
              errorMessage={
                errors[getEmploymentVariableName(CurrentEmploymentVariable.EmployerName, borrowerType, currentIndex)]
                  ?.message
              }
              inputContainerClassName={styles.input}
              onChange={(event) => {
                clearErrors(
                  getEmploymentVariableName(CurrentEmploymentVariable.EmployerName, borrowerType, currentIndex),
                );
                setValue(
                  getEmploymentVariableName(CurrentEmploymentVariable.EmployerName, borrowerType, currentIndex),
                  event.target.value,
                );
              }}
              disabled={getIsInitialValueNotNull(
                applicationVariables[
                  getEmploymentVariableName(CurrentEmploymentVariable.EmployerName, borrowerType, currentIndex)
                ],
              )}
            />
            <Input
              label={CurrentEmploymentInputLabel.Position}
              name={getEmploymentVariableName(CurrentEmploymentVariable.Position, borrowerType, currentIndex)}
              value={watcher[getEmploymentVariableName(CurrentEmploymentVariable.Position, borrowerType, currentIndex)]}
              errorMessage={
                errors[getEmploymentVariableName(CurrentEmploymentVariable.Position, borrowerType, currentIndex)]
                  ?.message
              }
              inputContainerClassName={styles.input}
              disabled={getIsInitialValueNotNull(
                applicationVariables[
                  getEmploymentVariableName(CurrentEmploymentVariable.Position, borrowerType, currentIndex)
                ],
              )}
              onChange={(event) => {
                clearErrors(getEmploymentVariableName(CurrentEmploymentVariable.Position, borrowerType, currentIndex));
                setValue(
                  getEmploymentVariableName(CurrentEmploymentVariable.Position, borrowerType, currentIndex),
                  event.target.value,
                );
              }}
            />
            {!isOnlyPartial && (
              <PhoneNumberInput
                label={CurrentEmploymentInputLabel.EmployerPhone}
                name={getEmploymentVariableName(CurrentEmploymentVariable.EmployerPhone, borrowerType, currentIndex)}
                className={styles.input}
                inputContainerClassname={styles.input}
                value={
                  watcher[
                    getEmploymentVariableName(CurrentEmploymentVariable.EmployerPhone, borrowerType, currentIndex)
                  ]
                }
                errorMessage={
                  errors[getEmploymentVariableName(CurrentEmploymentVariable.EmployerPhone, borrowerType, currentIndex)]
                    ?.message
                }
                disabled={getIsInitialValueNotNull(
                  applicationVariables[
                    getEmploymentVariableName(CurrentEmploymentVariable.EmployerPhone, borrowerType, currentIndex)
                  ],
                )}
                onChange={(value: string) => {
                  clearErrors(
                    getEmploymentVariableName(CurrentEmploymentVariable.EmployerPhone, borrowerType, currentIndex),
                  );
                  setValue(
                    getEmploymentVariableName(CurrentEmploymentVariable.EmployerPhone, borrowerType, currentIndex),
                    value,
                  );
                }}
                onBlur={() => {
                  if (
                    watcher[
                      getEmploymentVariableName(CurrentEmploymentVariable.EmployerPhone, borrowerType, currentIndex)
                    ]
                  ) {
                    trigger(
                      getEmploymentVariableName(CurrentEmploymentVariable.EmployerPhone, borrowerType, currentIndex),
                    );
                  }
                }}
              />
            )}
            <NumberInput
              label={CurrentEmploymentInputLabel.HireDate}
              name={getEmploymentVariableName(CurrentEmploymentVariable.HireDate, borrowerType, currentIndex)}
              value={watcher[getEmploymentVariableName(CurrentEmploymentVariable.HireDate, borrowerType, currentIndex)]}
              errorMessage={
                errors[getEmploymentVariableName(CurrentEmploymentVariable.HireDate, borrowerType, currentIndex)]
                  ?.message
              }
              disabled={getIsInitialValueNotNull(
                applicationVariables[
                  getEmploymentVariableName(CurrentEmploymentVariable.HireDate, borrowerType, currentIndex)
                ],
              )}
              className={styles.input}
              onChange={(event) => {
                clearErrors(getEmploymentVariableName(CurrentEmploymentVariable.HireDate, borrowerType, currentIndex));
                setValue(
                  getEmploymentVariableName(CurrentEmploymentVariable.HireDate, borrowerType, currentIndex),
                  event.target.value,
                );
              }}
              onBlur={() => {
                if (
                  watcher[getEmploymentVariableName(CurrentEmploymentVariable.HireDate, borrowerType, currentIndex)]
                ) {
                  trigger(getEmploymentVariableName(CurrentEmploymentVariable.HireDate, borrowerType, currentIndex));
                }
              }}
              placeholder="MM/YYYY"
              format="##/####"
              mask="_"
              allowEmptyFormatting
            />
          </div>
          {!isOnlyPartial && (
            <SearchLocationInput
              containerClassName={styles.select_input}
              initialValue={{
                streetAddress:
                  watcher[getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, currentIndex)],
                state: watcher[getEmploymentVariableName(CurrentEmploymentVariable.State, borrowerType, currentIndex)],
                city: watcher[getEmploymentVariableName(CurrentEmploymentVariable.City, borrowerType, currentIndex)],
                zip: watcher[getEmploymentVariableName(CurrentEmploymentVariable.Zip, borrowerType, currentIndex)],
              }}
              streetLabel={CurrentEmploymentInputLabel.Address}
              zipLabel={CurrentEmploymentInputLabel.Zip}
              cityLabel={CurrentEmploymentInputLabel.City}
              stateLabel={CurrentEmploymentInputLabel.State}
              control={control}
              useFormControl
              inputNames={{
                streetAddress: getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, currentIndex),
                city: getEmploymentVariableName(CurrentEmploymentVariable.City, borrowerType, currentIndex),
                state: getEmploymentVariableName(CurrentEmploymentVariable.State, borrowerType, currentIndex),
                zip: getEmploymentVariableName(CurrentEmploymentVariable.Zip, borrowerType, currentIndex),
              }}
              required
              errors={{
                streetAddress:
                  errors[getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, currentIndex)],
                state: errors[getEmploymentVariableName(CurrentEmploymentVariable.State, borrowerType, currentIndex)],
                city: errors[getEmploymentVariableName(CurrentEmploymentVariable.City, borrowerType, currentIndex)],
                zip: errors[getEmploymentVariableName(CurrentEmploymentVariable.Zip, borrowerType, currentIndex)],
              }}
              inputChange={(name, value) => {
                setValue(name as any, value);

                if (formState.isSubmitted) {
                  trigger(name as any);
                }
              }}
              getValues={getValues}
              disabled={getIsAddressInformationDisabled({
                city: applicationVariables[
                  getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, currentIndex)
                ]?.city,
                state_or_province:
                  applicationVariables[
                    getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, currentIndex)
                  ]?.state_or_province,
                street_name:
                  applicationVariables[
                    getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, currentIndex)
                  ]?.street_name,
                zip_or_postal_code:
                  applicationVariables[
                    getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, currentIndex)
                  ]?.zip_or_postal_code,
              })}
            />
          )}
        </div>
      );
    });
  };

  const renderEmploymentStatusInformation = () => {
    const employmentStatus =
      borrowerType === BorrowerRole.CoBorrower
        ? applicationVariables?.coborrower_employment_status
        : applicationVariables?.borrower_employment_status;

    switch (employmentStatus) {
      case BorrowerEmploymentStatus.GovernmentW2: {
        return <Government watcher={watcher} borrowerType={borrowerType} formMethods={formMethods} />;
      }
      case BorrowerEmploymentStatus.ActiveMilitaryW2: {
        return <ActiveMilitary watcher={watcher} borrowerType={borrowerType} formMethods={formMethods} />;
      }
      case BorrowerEmploymentStatus.RetiredMilitary: {
        return <RetiredMilitary watcher={watcher} borrowerType={borrowerType} formMethods={formMethods} />;
      }
      case BorrowerEmploymentStatus.RetiredNonMilitary: {
        return <RetiredNonMilitary watcher={watcher} borrowerType={borrowerType} formMethods={formMethods} />;
      }
      default: {
        return <></>;
      }
    }
  };

  return (
    <div>
      {(isCurrentEmploymentsRequired || isPartialCurrentEmploymentsRequired) && (
        <div className={styles.infoContainer}>
          <div className={styles.formHeader}>Current Employer Information</div>
          {renderEmployments()}
        </div>
      )}
      {isPreviousEmploymentRequired && (
        <div className={styles.infoContainer}>
          <div className={styles.formHeader}>Previous Employer Information</div>
          <PreviousEmploymentInformation watcher={watcher} borrowerType={borrowerType} formMethods={formMethods} />
        </div>
      )}
      {isEmploymentStatusInformationRequired && (
        <div className={styles.infoContainer}>
          <div className={styles.formHeader}>
            {(borrowerType === BorrowerRole.CoBorrower
              ? applicationVariables.coborrower_employment_status
              : applicationVariables.borrower_employment_status) + ' Information'}
          </div>
          {renderEmploymentStatusInformation()}
        </div>
      )}
      {!getIsEmploymentSectionCompleted(dashboardAdditionalInfo!, borrowerType) && (
        <Button onClick={saveEmploymentsInformation}>Save</Button>
      )}
    </div>
  );
};

export default EmploymentsInformation;
